import { Mic, Printer, RefreshCw, Volume2 } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/AppShell';
import { PageCaption } from '../components/PageCaption';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Empty } from '../components/ui/empty';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import { useToast } from '../components/ui/toast';
import {
  type PresenterBriefItem,
  type PresenterSlot,
  useRegeneratePresenterBrief,
  usePresenterBriefs,
} from '../lib/api/presenterBrief';
import { fmtDateTime, fmtRelative } from '../lib/date';
import { useLocaleStore } from '../lib/locales';

const SLOT_LABEL: Record<PresenterSlot, string> = {
  morning: 'Morning',
  midday: 'Midday',
  evening: 'Evening',
  late: 'Late',
};

const SLOT_BULLETIN_HINT: Record<PresenterSlot, string> = {
  morning: 'for ~06:00 bulletin',
  midday: 'for ~13:00 bulletin',
  evening: 'for ~18:00 bulletin',
  late: 'for ~23:00 bulletin',
};

export default function PresenterBrief(): JSX.Element {
  const locale = useLocaleStore((s) => s.current);
  const [slotFilter, setSlotFilter] = useState<'all' | PresenterSlot>('all');
  const briefs = usePresenterBriefs({
    locale,
    slot: slotFilter === 'all' ? undefined : slotFilter,
    limit: 30,
  });

  const cfg = briefs.data?.config;
  const items = briefs.data?.items ?? [];

  // The "current" brief = first item for today's Lagos date matching the
  // selected slot filter (or any slot if "all"). It gets the hero treatment.
  const todayLagos = new Date(Date.now() + 60 * 60_000).toISOString().slice(0, 10);
  const todayItems = items.filter((b) => b.lagosDate === todayLagos);
  const olderItems = items.filter((b) => b.lagosDate !== todayLagos);

  return (
    <>
      <Topbar title="Presenter brief" subtitle={`Locale ${locale.toUpperCase()}`} />
      <PageHeader
        title="Presenter brief"
        subtitle={
          cfg?.enabled
            ? `Auto-generated by Groq · ${todayItems.length}/4 slots ready for today`
            : 'Disabled — set PRESENTER_BRIEF_ENABLED=true to enable.'
        }
        actions={
          <Select value={slotFilter} onValueChange={(v) => setSlotFilter(v as typeof slotFilter)}>
            <SelectTrigger className="h-8 w-[140px] text-[12.5px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All slots</SelectItem>
              <SelectItem value="morning">Morning</SelectItem>
              <SelectItem value="midday">Midday</SelectItem>
              <SelectItem value="evening">Evening</SelectItem>
              <SelectItem value="late">Late</SelectItem>
            </SelectContent>
          </Select>
        }
      />
      <PageCaption id="presenter-brief" icon={Mic}>
        <strong className="text-ink">Presenter brief</strong> — auto-generated talking
        points your radio and TV presenters can read straight off the dashboard
        before each bulletin. Refreshed 60–90 minutes ahead of the morning,
        midday, evening, and late slots so the on-air desk is always aligned with
        the day's top stories.
      </PageCaption>
      <div className="space-y-5 px-6 pb-10">
        {briefs.isLoading ? (
          <Card className="p-6"><Spinner label="Loading…" /></Card>
        ) : items.length === 0 ? (
          <Empty
            title="No briefs yet"
            description={
              cfg?.enabled
                ? 'Briefs are generated 60–90 minutes before each bulletin window. The next one will appear shortly after its slot time.'
                : 'Enable the scheduler in your environment to start generating briefs automatically.'
            }
          />
        ) : (
          <>
            {todayItems.length > 0 ? (
              <section className="space-y-4">
                <p className="eyebrow">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand" />
                  Today · {todayLagos}
                </p>
                {todayItems.map((b) => (
                  <BriefCard key={b.id} brief={b} hero />
                ))}
              </section>
            ) : null}

            {olderItems.length > 0 ? (
              <section className="space-y-3">
                <p className="eyebrow">
                  <span className="h-1.5 w-1.5 rounded-full bg-ink-subtle/40" />
                  Earlier
                </p>
                {olderItems.map((b) => (
                  <BriefCard key={b.id} brief={b} hero={false} />
                ))}
              </section>
            ) : null}
          </>
        )}

        {cfg ? <SlotScheduleStrip slotTimes={cfg.slotTimes} /> : null}
      </div>
    </>
  );
}

function BriefCard({ brief, hero }: { brief: PresenterBriefItem; hero: boolean }): JSX.Element {
  const regen = useRegeneratePresenterBrief();
  const { showToast } = useToast();
  const cardRef = useRef<HTMLDivElement>(null);

  const handleRegen = async () => {
    try {
      const out = await regen.mutateAsync({ locale: brief.locale, slot: brief.slot });
      showToast({
        title: out.status === 'created' ? 'Brief regenerated' : `Skipped (${out.status})`,
        description: out.reason ?? `${brief.locale} · ${brief.slot}`,
        variant: out.status === 'created' ? 'success' : 'danger',
      });
    } catch (err) {
      showToast({
        title: 'Regenerate failed',
        description: (err as Error).message,
        variant: 'danger',
      });
    }
  };

  /**
   * Scope `window.print()` to just this brief by marking its DOM root with
   * `data-print-target` and toggling a body-level class that the print
   * stylesheet uses to hide everything else. Cleanup runs on `afterprint`
   * so successive prints from different briefs work correctly.
   */
  const handlePrint = useCallback(() => {
    const card = cardRef.current;
    if (!card) {
      window.print();
      return;
    }
    card.setAttribute('data-print-target', '');
    document.body.classList.add('print-single-target');
    const cleanup = () => {
      card.removeAttribute('data-print-target');
      document.body.classList.remove('print-single-target');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    // Defer to next tick so the class actually flushes to the DOM before
    // the print dialog's style snapshot runs — Safari occasionally screen-
    // shots a stale layout otherwise.
    requestAnimationFrame(() => window.print());
  }, []);

  return (
    <Card
      ref={cardRef}
      className={
        hero
          ? 'overflow-hidden border-brand/25 bg-gradient-to-br from-brand-soft/40 via-surface to-surface shadow-elevated'
          : 'overflow-hidden'
      }
    >
      {/* Header band */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line/70 px-5 py-3">
        <div className="flex flex-wrap items-baseline gap-2">
          <Mic className={hero ? 'h-4 w-4 text-brand' : 'h-4 w-4 text-ink-subtle'} />
          <h2 className="font-heading text-[16px] font-semibold tracking-[-0.012em] text-ink">
            {SLOT_LABEL[brief.slot]} brief
          </h2>
          <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-subtle">
            {SLOT_BULLETIN_HINT[brief.slot]} · {brief.locale.toUpperCase()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink-subtle">
            generated {fmtRelative(brief.generatedAt)}
          </span>
          <Button size="sm" variant="ghost" onClick={handlePrint}>
            <Printer className="h-3.5 w-3.5" /> Print
          </Button>
          <Button size="sm" variant="outline" disabled={regen.isPending} onClick={handleRegen}>
            <RefreshCw className={`h-3.5 w-3.5 ${regen.isPending ? 'animate-spin' : ''}`} />
            Regenerate
          </Button>
        </div>
      </div>

      {/* Themes line */}
      {brief.content.themesToday ? (
        <div className="border-b border-line/50 bg-surface-tinted/40 px-5 py-2.5">
          <p className="text-[13px] font-medium italic leading-snug text-ink-muted">
            <span className="not-italic font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-subtle">
              Themes ·{' '}
            </span>
            {brief.content.themesToday}
          </p>
        </div>
      ) : null}

      {/* Stories */}
      <ol className="divide-y divide-line/60">
        {brief.stories.map((s, i) => (
          <li key={s.postId} className="flex gap-4 px-5 py-4">
            <div className="shrink-0">
              <span className="font-heading text-[24px] font-bold leading-none tracking-tight text-brand tabular">
                {String(i + 1).padStart(2, '0')}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-2">
                {s.slug ? (
                  <Link
                    to={`/posts/${s.slug}`}
                    className="font-heading text-[15px] font-semibold leading-snug text-ink hover:text-brand"
                  >
                    {s.title}
                  </Link>
                ) : (
                  <span className="font-heading text-[15px] font-semibold leading-snug text-ink">
                    {s.title}
                  </span>
                )}
                {s.publishedAt ? (
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-ink-subtle">
                    {fmtRelative(s.publishedAt)}
                  </span>
                ) : null}
              </div>
              {s.lead ? (
                <p className="mt-1.5 text-[14px] font-medium leading-relaxed text-ink">
                  <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.18em] text-brand">
                    LEAD ·{' '}
                  </span>
                  {s.lead}
                </p>
              ) : null}
              {s.summaryBullets.length > 0 ? (
                <ul className="mt-2 space-y-1">
                  {s.summaryBullets.map((b, bi) => (
                    <li key={bi} className="flex gap-2 text-[13px] leading-snug text-ink-muted">
                      <span className="select-none text-brand/70">▸</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {s.echoSources.length > 0 ? (
                <p className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.14em] text-ink-subtle">
                    Also reporting:
                  </span>
                  {s.echoSources.map((src) => (
                    <Badge key={src} variant="neutral" className="font-mono text-[9.5px]">
                      {src}
                    </Badge>
                  ))}
                </p>
              ) : null}
            </div>
          </li>
        ))}
      </ol>

      {/* Pronunciation guide */}
      {brief.content.pronunciationGuide.length > 0 ? (
        <div className="border-t border-line/60 bg-surface-tinted/30 px-5 py-3">
          <div className="mb-2 flex items-center gap-1.5">
            <Volume2 className="h-3.5 w-3.5 text-brand" />
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-subtle">
              Pronunciation guide
            </p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {brief.content.pronunciationGuide.map((p) => (
              <span key={p.term} className="text-[12.5px] text-ink">
                <span className="font-semibold">{p.term}</span>
                <span className="font-mono text-[10.5px] text-ink-muted"> · {p.phonetic}</span>
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {/* Footer meta */}
      <div className="flex items-center justify-between border-t border-line/40 px-5 py-2 font-mono text-[9.5px] uppercase tracking-[0.14em] text-ink-subtle/80">
        <span>{brief.groqModel}</span>
        <span>{fmtDateTime(brief.generatedAt)} · Lagos {brief.lagosDate}</span>
      </div>
    </Card>
  );
}

function SlotScheduleStrip({
  slotTimes,
}: {
  slotTimes: Record<PresenterSlot, { hour: number; minute: number }>;
}): JSX.Element {
  const slots: PresenterSlot[] = ['morning', 'midday', 'evening', 'late'];
  return (
    <Card className="p-4">
      <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-subtle">
        Daily generation schedule (Africa/Lagos)
      </p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {slots.map((s) => {
          const t = slotTimes[s];
          return (
            <div key={s} className="rounded-md border border-line bg-surface-tinted/40 px-3 py-2">
              <p className="text-[12.5px] font-semibold text-ink">{SLOT_LABEL[s]}</p>
              <p className="font-mono text-[12px] tabular text-brand">
                {String(t.hour).padStart(2, '0')}:{String(t.minute).padStart(2, '0')}
              </p>
              <p className="mt-0.5 font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink-subtle">
                {SLOT_BULLETIN_HINT[s]}
              </p>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
